import { useState, useEffect } from "react"
import {
  fetchTrainings,
  createTraining,
  updateTraining,
  deleteTraining,
  toggleTraining,
  type Training,
  type TrainingRequiredInput,
} from "@/lib/supportAdminApi"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"
import { Brain, Plus, Pencil, Trash2, X } from "lucide-react"

// ─── Types ──────────────────────────────────────────────────────────────────

const INPUT_TYPE_LABELS: Record<string, string> = {
  email: "E-mail",
  text: "Texto",
  product_select: "Seleção de produto",
}

const RESOLUTION_LABELS: Record<string, string> = {
  create_ticket: "Criar ticket",
  respond: "Responder",
}

// ─── RequiredInputsBuilder ──────────────────────────────────────────────────

function RequiredInputsBuilder({
  inputs,
  onChange,
}: {
  inputs: TrainingRequiredInput[]
  onChange: (inputs: TrainingRequiredInput[]) => void
}) {
  const addInput = () => {
    onChange([...inputs, { name: "", type: "text", description: "" }])
  }

  const updateInput = (index: number, field: keyof TrainingRequiredInput, value: string) => {
    const updated = inputs.map((inp, i) =>
      i === index ? { ...inp, [field]: value } : inp
    )
    onChange(updated)
  }

  const removeInput = (index: number) => {
    onChange(inputs.filter((_, i) => i !== index))
  }

  return (
    <div className="space-y-3">
      <Label>Informações necessárias</Label>
      {inputs.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Nenhuma informação configurada. A IA decidirá o que perguntar baseado nas instruções.
        </p>
      )}
      {inputs.map((inp, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            className="w-28"
            placeholder="nome"
            value={inp.name}
            onChange={(e) => updateInput(i, "name", e.target.value)}
          />
          <Select
            value={inp.type}
            onValueChange={(v) => updateInput(i, "type", v)}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="email">E-mail</SelectItem>
              <SelectItem value="text">Texto</SelectItem>
              <SelectItem value="product_select">Seleção de produto</SelectItem>
            </SelectContent>
          </Select>
          <Input
            className="flex-1"
            placeholder="Descrição"
            value={inp.description}
            onChange={(e) => updateInput(i, "description", e.target.value)}
          />
          <Button variant="ghost" size="icon" onClick={() => removeInput(i)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addInput}>
        <Plus className="h-3.5 w-3.5 mr-1" /> Adicionar
      </Button>
    </div>
  )
}

// ─── TrainingDialog ─────────────────────────────────────────────────────────

function TrainingDialog({
  open,
  onOpenChange,
  editing,
  onSave,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  editing: Training | null
  onSave: () => void
}) {
  const { toast } = useToast()
  const [saving, setSaving] = useState(false)

  const [name, setName] = useState("")
  const [scenario, setScenario] = useState("")
  const [instructions, setInstructions] = useState("")
  const [requiredInputs, setRequiredInputs] = useState<TrainingRequiredInput[]>([])
  const [resolutionAction, setResolutionAction] = useState("create_ticket")
  const [resolutionMessage, setResolutionMessage] = useState("")
  const [priority, setPriority] = useState(0)

  useEffect(() => {
    if (editing) {
      setName(editing.name)
      setScenario(editing.scenario)
      setInstructions(editing.instructions)
      setRequiredInputs(editing.required_inputs as TrainingRequiredInput[])
      setResolutionAction(editing.resolution_action)
      setResolutionMessage(editing.resolution_message || "")
      setPriority(editing.priority)
    } else {
      setName("")
      setScenario("")
      setInstructions("")
      setRequiredInputs([])
      setResolutionAction("create_ticket")
      setResolutionMessage("")
      setPriority(0)
    }
  }, [editing, open])

  const handleSave = async () => {
    if (!name.trim() || !scenario.trim() || !instructions.trim()) {
      toast({ title: "Preencha nome, cenário e instruções", variant: "destructive" })
      return
    }

    setSaving(true)
    try {
      const payload = {
        name: name.trim(),
        scenario: scenario.trim(),
        instructions: instructions.trim(),
        required_inputs: requiredInputs.filter((i) => i.name.trim()),
        resolution_action: resolutionAction,
        resolution_message: resolutionMessage.trim() || undefined,
        priority,
      }

      if (editing) {
        await updateTraining(editing.id, payload)
        toast({ title: "Treinamento atualizado" })
      } else {
        await createTraining(payload)
        toast({ title: "Treinamento criado" })
      }
      onSave()
      onOpenChange(false)
    } catch (err) {
      toast({ title: "Erro ao salvar", description: String(err), variant: "destructive" })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar treinamento" : "Novo treinamento"}</DialogTitle>
          <DialogDescription>
            Ensine a IA como agir em um cenário específico.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="grid grid-cols-[1fr_100px] gap-3">
            <div>
              <Label>Nome</Label>
              <Input
                placeholder="Ex: Cancelamento de curso"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <Label>Prioridade</Label>
              <Input
                type="number"
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value))}
              />
            </div>
          </div>

          <div>
            <Label>Quando isso se aplica? (Cenário)</Label>
            <Textarea
              placeholder="Ex: Quando o aluno quer cancelar um curso, pedir reembolso, ou desistir de uma compra."
              value={scenario}
              onChange={(e) => setScenario(e.target.value)}
              rows={3}
            />
          </div>

          <div>
            <Label>O que a IA deve fazer? (Instruções)</Label>
            <Textarea
              placeholder={"Ex: 1. Cumprimentar e demonstrar empatia\n2. Pedir o e-mail cadastrado na Hotmart\n3. Buscar os produtos do aluno\n4. Pedir para selecionar qual produto quer cancelar\n5. Perguntar o motivo do cancelamento\n6. Criar ticket de suporte com todas as informações"}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={6}
            />
          </div>

          <RequiredInputsBuilder inputs={requiredInputs} onChange={setRequiredInputs} />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Ação ao concluir</Label>
              <Select value={resolutionAction} onValueChange={setResolutionAction}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="create_ticket">Criar ticket</SelectItem>
                  <SelectItem value="respond">Responder</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Mensagem final (opcional)</Label>
            <Textarea
              placeholder="Ex: Ticket {ticket_number} criado! Vamos resolver o cancelamento do {product_name}."
              value={resolutionMessage}
              onChange={(e) => setResolutionMessage(e.target.value)}
              rows={3}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Variáveis: {"{email}"} {"{product_name}"} {"{reason}"} {"{ticket_number}"} {"{buyer_name}"}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Salvando..." : editing ? "Salvar" : "Criar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function AdminTreinamentos() {
  const { toast } = useToast()
  const [trainings, setTrainings] = useState<Training[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Training | null>(null)

  const load = async () => {
    try {
      setTrainings(await fetchTrainings())
    } catch (err) {
      toast({ title: "Erro ao carregar treinamentos", variant: "destructive" })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const handleToggle = async (t: Training) => {
    try {
      const updated = await toggleTraining(t.id)
      setTrainings((prev) => prev.map((x) => (x.id === t.id ? updated : x)))
    } catch {
      toast({ title: "Erro ao alterar status", variant: "destructive" })
    }
  }

  const handleDelete = async (t: Training) => {
    if (!confirm(`Excluir treinamento "${t.name}"?`)) return
    try {
      await deleteTraining(t.id)
      setTrainings((prev) => prev.filter((x) => x.id !== t.id))
      toast({ title: "Treinamento excluído" })
    } catch {
      toast({ title: "Erro ao excluir", variant: "destructive" })
    }
  }

  const openCreate = () => {
    setEditing(null)
    setDialogOpen(true)
  }

  const openEdit = (t: Training) => {
    setEditing(t)
    setDialogOpen(true)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="h-6 w-6" /> Treinamentos
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Ensine a IA como agir em cenários específicos. Quando um cenário é identificado, a IA segue as instruções automaticamente.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" /> Novo treinamento
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Cenários configurados</CardTitle>
          <CardDescription>
            {trainings.length} treinamento(s) cadastrado(s)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Carregando...</p>
          ) : trainings.length === 0 ? (
            <div className="text-center py-12">
              <Brain className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                Nenhum treinamento criado ainda. Crie o primeiro para ensinar a IA a lidar com cenários específicos.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Cenário</TableHead>
                  <TableHead className="text-center">Inputs</TableHead>
                  <TableHead>Ação</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trainings.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-medium">{t.name}</TableCell>
                    <TableCell className="max-w-[250px] truncate text-muted-foreground text-sm">
                      {t.scenario}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center gap-1 flex-wrap">
                        {t.required_inputs.map((inp, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {INPUT_TYPE_LABELS[(inp as TrainingRequiredInput).type] || (inp as TrainingRequiredInput).type}
                          </Badge>
                        ))}
                        {t.required_inputs.length === 0 && (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {RESOLUTION_LABELS[t.resolution_action] || t.resolution_action}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={t.is_enabled}
                        onCheckedChange={() => handleToggle(t)}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(t)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(t)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <TrainingDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        onSave={load}
      />
    </div>
  )
}
